import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { RefreshCw } from 'lucide-react';
import { apiClient } from '../../utils/apiHelper';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DepreciationSetting = () => {
  const [assets, setAssets] = useState([]);

  useEffect(() => { fetchAssets(); }, []);

  const fetchAssets = async () => {
    try {
      const response = await apiClient.get('/api/accounts/assets'); setAssets(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const calculateDepreciation = (asset) => {
    const yearsOwned = new Date().getFullYear() - new Date(asset.purchase_date).getFullYear();
    const annualDepreciation = (asset.purchase_value - asset.salvage_value) * (asset.depreciation_rate / 100);
    const totalDepreciation = annualDepreciation * yearsOwned;
    const currentValue = Math.max(asset.purchase_value - totalDepreciation, asset.salvage_value);
    return { annualDepreciation, totalDepreciation, currentValue, yearsOwned };
  };

  return (
    <div className="p-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Depreciation Settings</CardTitle>
          <Button onClick={fetchAssets}>
            <RefreshCw className="w-4 h-4 mr-2" />Refresh
          </Button>
        </CardHeader>
        <CardContent>
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b">
                <th className="text-left p-3">Asset Name</th>
                <th className="text-left p-3">Purchase Value</th>
                <th className="text-left p-3">Depreciation Rate</th>
                <th className="text-left p-3">Years Owned</th>
                <th className="text-left p-3">Annual Depreciation</th>
                <th className="text-left p-3">Total Depreciation</th>
                <th className="text-left p-3">Calculated Value</th>
              </tr>
            </thead>
            <tbody>
              {assets.length === 0 ? (
                <tr><td colSpan="7" className="text-center p-4 text-gray-500">No assets found.</td></tr>
              ) : (
                assets.map((asset) => {
                  const dep = calculateDepreciation(asset);
                  return (
                    <tr key={asset.id} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-medium">{asset.asset_name}</td>
                      <td className="p-3">₹{asset.purchase_value.toFixed(2)}</td>
                      <td className="p-3">{asset.depreciation_rate}%</td>
                      <td className="p-3">{dep.yearsOwned}</td>
                      <td className="p-3">₹{dep.annualDepreciation.toFixed(2)}</td>
                      <td className="p-3 text-red-600">₹{dep.totalDepreciation.toFixed(2)}</td>
                      <td className="p-3 font-semibold text-green-600">₹{dep.currentValue.toFixed(2)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h3 className="font-semibold mb-2">Depreciation Methods:</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Straight Line Method: Annual Depreciation = (Purchase Value - Salvage Value) × Rate</li>
              <li>• Total Depreciation = Annual Depreciation × Years Owned</li>
              <li>• Current Value = Purchase Value - Total Depreciation</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DepreciationSetting;